import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DomainOrder, getMyDomains } from "../../services/domains";
import { PixIcon } from "../Subscription/pages/Checkout/icons";
import { Card, Code, QrCode, Status, Steps } from "../Subscription/pages/PaymentPending/styles";

// While the Pix is on screen, the payment is checked this often
const POLL_MS = 4000;

type Props = {
  order: DomainOrder;
  total: string;
  /** A new Pix (the one shown expired). */
  onRenew: () => void;
  /** Paid: the domain is being registered. */
  onPaid: () => void;
};

/**
 * Paying a domain with Pix (the PLUS waiting screen's look): the QR code and the copy-and-paste code for 30
 * minutes, then a new one. The screen moves on by itself once the payment arrives (Stripe → API webhook).
 */
export const PixPayment = ({ order, total, onRenew, onPaid }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.subscription.pending";
  const domainPrefix = "pages.domains.checkout.pix";
  const expiresAt = useRef(
    order.pix?.expiresAt ? new Date(order.pix.expiresAt).getTime() : Date.now() + 30 * 60 * 1000
  ).current;
  const secondsUntil = () => Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
  const [secondsLeft, setSecondsLeft] = useState(secondsUntil);
  const [copied, setCopied] = useState(false);
  const [checkState, setCheckState] = useState<"idle" | "checking" | "notYet">("idle");
  const isExpired = secondsLeft <= 0;
  // Once it's paid (or the screen is gone) nothing else may call onPaid, even from a request still in flight
  const isDone = useRef(false);

  // From the clock, not from counting ticks: a throttled background tab catches up when it's back
  useEffect(() => {
    if (isExpired) {
      return;
    }
    const timer = window.setInterval(() => setSecondsLeft(secondsUntil()), 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpired]);

  const isPaid = async () => {
    const domains = await getMyDomains().catch(() => []);
    const mine = domains.find(item => item.domain === order.domain);
    return !!mine && !mine.paymentPending;
  };

  const paid = () => {
    if (isDone.current) {
      return;
    }
    isDone.current = true;
    onPaid();
  };

  useEffect(() => {
    isDone.current = false;
    let isChecking = false;
    const timer = window.setInterval(async () => {
      if (isChecking || isDone.current) {
        return;
      }
      isChecking = true;
      try {
        if ((await isPaid()) && !isDone.current) {
          paid();
        }
      } finally {
        isChecking = false;
      }
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
      isDone.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.domainId]);

  const checkPayment = async () => {
    setCheckState("checking");
    if (await isPaid()) {
      paid();
      return;
    }
    if (!isDone.current) {
      setCheckState("notYet");
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(order.pix?.code || "");
    } catch {
      // Not allowed or no clipboard: the code is on screen to be selected, and "Copiado" would be a lie
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const countdown = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

  return (
    <>
      <header style={{ textAlign: "center" }}>
        <span className="method-icon">
          <PixIcon width={28} height={28} />
        </span>
        <h1>{t(`${domainPrefix}.title`, { domain: order.domain })}</h1>
        <p>{t(`${prefix}.pix.subtitle`)}</p>
      </header>

      <Card>
        <div className="amount">
          <span>{order.domain}</span>
          <b>{total}</b>
        </div>
        <QrCode $expired={isExpired}>
          {order.pix?.qrImageUrl && <img src={order.pix.qrImageUrl} alt={t(`${domainPrefix}.qrAlt`)} />}
          {isExpired && <span className="expired">{t(`${prefix}.pix.expired`)}</span>}
        </QrCode>
        {isExpired ? (
          <button type="button" className="primary" onClick={onRenew}>
            {t(`${prefix}.pix.newCode`)}
          </button>
        ) : (
          <>
            <p className="timer">{t(`${prefix}.pix.expiresIn`, { time: countdown })}</p>
            <Code>
              <span className="label">{t(`${prefix}.pix.copyPaste`)}</span>
              <code>{order.pix?.code}</code>
            </Code>
            <button type="button" className="primary" onClick={copy}>
              {copied ? t(`${prefix}.copied`) : t(`${prefix}.pix.copy`)}
            </button>
          </>
        )}
      </Card>

      <Steps>
        <li>
          <span className="number">1</span>
          {t(`${prefix}.pix.steps.1`)}
        </li>
        <li>
          <span className="number">2</span>
          {t(`${prefix}.pix.steps.2`)}
        </li>
        <li>
          <span className="number">3</span>
          {t(`${domainPrefix}.step3`)}
        </li>
      </Steps>

      <Status>
        <span className="dot" aria-hidden="true" />
        <span className="text">
          <b>{t(`${prefix}.pix.waiting`)}</b>
          <span>{checkState === "notYet" ? t(`${prefix}.pix.notYet`) : t(`${domainPrefix}.waiting`)}</span>
        </span>
        <button type="button" disabled={checkState === "checking"} onClick={checkPayment}>
          {checkState === "checking" ? t(`${prefix}.pix.checking`) : t(`${prefix}.pix.paid`)}
        </button>
      </Status>
    </>
  );
};
